import React, { useState, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { processQRCode } from '../services/qrService';

interface QRScannerProps {
  onSuccess: (qrNumber: string) => void;
  onError: (error: string) => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onSuccess, onError }) => {
  const [scanning, setScanning] = useState(false);
  const [qrScanner, setQrScanner] = useState<Html5Qrcode | null>(null);
  const [cameraSupported, setCameraSupported] = useState<boolean | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');

  // Function to start scanning with a given scanner instance
  const startScanningWithScanner = (scanner: Html5Qrcode) => {
    setScanning(true);
    setScanStatus('idle');
    
    const qrConfig = { fps: 10, qrbox: { width: 250, height: 250 } };
    
    scanner.start(
      { facingMode: "environment" },
      qrConfig,
      async (decodedText) => {
        // Show processing state
        setScanStatus('processing');
        console.log("QR Code detected:", decodedText);
        
        // Process the QR code
        const result = await processQRCode(decodedText);
        
        if (result.success && result.qrNumber) {
          setScanStatus('success');
          console.log("Valid QR code:", result.qrNumber);
          
          // Wait a moment to show success feedback
          setTimeout(() => {
            onSuccess(result.qrNumber!);
            scanner.stop().catch(err => console.error("Error stopping scanner:", err));
            setScanning(false);
          }, 500);
        } else {
          setScanStatus('error');
          console.log("Invalid QR code");
          onError("Ungültiger QR-Code. Bitte versuchen Sie es erneut.");
          
          // Reset status after showing error
          setTimeout(() => {
            setScanStatus('idle');
          }, 2000);
        }
      },
      (_errorMessage) => {
        // Error while scanning (this is normal, just means no QR code detected yet)
        // console.log(errorMessage);
      }
    ).catch(err => {
      console.error("Error starting scanner:", err);
      
      // Set camera error based on error type
      if (err.name === "NotAllowedError") {
        setCameraError("Kamerazugriff wurde verweigert. Bitte erlauben Sie den Zugriff auf die Kamera in Ihren Browsereinstellungen.");
      } else if (err.name === "NotFoundError") {
        setCameraError("Keine Kamera gefunden. Bitte stellen Sie sicher, dass Ihr Gerät über eine Kamera verfügt.");
      } else {
        setCameraError(`Kamerafehler: ${err.message || "Unbekannter Fehler beim Zugriff auf die Kamera."}`);
      }
      
      setCameraSupported(false);
      setScanning(false);
      onError("Kamera konnte nicht gestartet werden. Bitte erlauben Sie den Zugriff auf Ihre Kamera.");
    });
  };

  useEffect(() => {
    // Check if camera is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraSupported(false);
      setCameraError("Ihr Browser unterstützt keine Kamerafunktion. Bitte verwenden Sie einen aktuellen Browser wie Chrome, Firefox oder Safari.");
      return;
    }

    // Initialize QR scanner and auto-start
    const scanner = new Html5Qrcode("qr-reader");
    setQrScanner(scanner);
    setCameraSupported(true);
    
    // Auto-start scanning after a short delay
    setTimeout(() => {
      startScanningWithScanner(scanner);
    }, 100);

    // Cleanup function to stop scanning when component unmounts
    return () => {
      if (scanner && scanning) {
        scanner.stop().catch(err => console.error("Error stopping scanner:", err));
      }
    };
  }, []); // Run only once on mount

  const startScanning = () => {
    if (!qrScanner) return;
    setScanStatus('idle');
    startScanningWithScanner(qrScanner);
  };

  const stopScanning = () => {
    if (qrScanner && scanning) {
      qrScanner.stop().then(() => {
        setScanning(false);
      }).catch(err => {
        console.error("Error stopping scanner:", err);
      });
    }
  };

  return (
    <div className="qr-scanner-container">
      {cameraError ? (
        <div className="camera-error-container">
          <div className="error-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h3>Kamera nicht verfügbar</h3>
          <p>{cameraError}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <>
          <div id="qr-reader" style={{ width: '100%', maxWidth: '400px' }}></div>
          
          {/* Scan status indicator */}
          {scanStatus !== 'idle' && (
            <div className={`scan-status scan-status-${scanStatus}`}>
              {scanStatus === 'processing' && (
                <>
                  <div className="status-icon">⏳</div>
                  <div>QR-Code wird verarbeitet...</div>
                </>
              )}
              {scanStatus === 'success' && (
                <>
                  <div className="status-icon">✓</div>
                  <div>Gültiger QR-Code!</div>
                </>
              )}
              {scanStatus === 'error' && (
                <>
                  <div className="status-icon">✗</div>
                  <div>Ungültiger QR-Code</div>
                </>
              )}
            </div>
          )}
          
          <div className="scanner-controls">
            {!scanning ? (
              <button 
                className="scan-button"
                onClick={startScanning}
                disabled={cameraSupported === false}
              >
                QR-Code Scannen
              </button>
            ) : (
              <button 
                className="stop-button"
                onClick={stopScanning}
              >
                Scannen Stoppen
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QRScanner;